/**
 * DocClassifier — framework-agnostic document-type classifier.
 *
 * This is a faithful port of src/app/docpic/cnn-classifier.ts's classify()
 * logic, lifted off the DOM (it operates on {@link ImagePixels} via the core
 * image helpers instead of canvases) and off Angular. The numeric behaviour is
 * IDENTICAL to the app:
 *   - CHW preprocess: square-resize to inputSize, per-channel (v/255-mean)/std,
 *   - softmax over the logits, energy = -logsumexp(logits),
 *   - OOD test: conf < oodMsp OR energy > oodEnergy,
 *   - confidence buckets: >=0.5 high, >=0.32 medium, else low,
 *   - rotate-on-low-confidence: classify upright; if OOD or conf < GOOD_CONF
 *     (0.5), also try 90/180/270 and keep whichever reads with the best conf,
 *   - experts = top-3 classes by probability.
 *
 * The model itself is injected as a {@link ClassifierModel} (logits in, run
 * anywhere), so this class never imports onnxruntime or the DOM.
 */
import { ImagePixels } from '../core/ports';
import { resizeImage, rotate90 } from '../preprocess/core/image';
import { pixelsToDataUrl } from '../preprocess/adapters/canvas-image';
import { ClassifierModel } from './ports';
import { ClassifierMeta, ClassifyDebug, DocPrediction } from './types';
import { documentTypeLabel } from './document-label';

/** Confidence at/above which the upright pass is trusted without rotating. */
const GOOD_CONF = 0.5;

function softmax(v: number[]): number[] {
  const m = Math.max(...v);
  const ex = v.map((x) => Math.exp(x - m));
  const s = ex.reduce((a, b) => a + b, 0) || 1;
  return ex.map((x) => x / s);
}

/** Energy score = -logsumexp(logits); higher = more out-of-distribution. */
function energyScore(logits: number[]): number {
  const m = Math.max(...logits);
  const lse = m + Math.log(logits.reduce((s, x) => s + Math.exp(x - m), 0));
  return -lse;
}

interface InferResult {
  order: [number, number][];
  top: number;
  conf: number;
  logits: number[];
  energy: number;
}

/** Per-stage trace the recognizer can hand in so it surfaces in the debug data. */
export interface StageImage {
  name: string;
  method: string;
  /** Stage output image as a data URL (already encoded), or ''. */
  img: string;
}

export class DocClassifier {
  constructor(
    private readonly model: ClassifierModel,
    private readonly meta: ClassifierMeta,
  ) {}

  /**
   * Classify a document image. `stages` is optional pipeline-trace context that
   * is copied verbatim into the returned {@link ClassifyDebug} (the recognizer
   * passes the pre-processing trace; standalone callers can omit it).
   */
  async classify(
    image: ImagePixels,
    stages: StageImage[] = [],
  ): Promise<{ prediction: DocPrediction; debug: ClassifyDebug }> {
    // The model is trained in ONE orientation (upright), so classify the upright
    // image first. Only if that pass isn't a confident match (low prob OR
    // out-of-distribution) do we try the other 3 rotations and keep whichever
    // reads best — test-time orientation correction, paid for only when weak.
    let best = await this.infer(image);
    let bestDeg = 0;
    const rotations = [{ deg: 0, label: this.meta.labels[best.top], conf: best.conf }];
    if (this.isOutOfDist(best) || best.conf < GOOD_CONF) {
      for (const deg of [90, 180, 270] as const) {
        const cand = await this.infer(rotate90(image, deg));
        rotations.push({ deg, label: this.meta.labels[cand.top], conf: cand.conf });
        if (cand.conf > best.conf) {
          best = cand;
          bestDeg = deg;
        }
      }
    }

    const fedImage = bestDeg ? rotate90(image, bestDeg as 90 | 180 | 270) : image;
    const ood = this.isOutOfDist(best);
    // The debug preview images are data URLs, which need a DOM canvas. Skip them
    // off the DOM (Node) so the classify path stays environment-agnostic — the
    // prediction is unaffected; only the optional preview strings are empty.
    const png = (img: ImagePixels): string =>
      typeof document === 'undefined' ? '' : pixelsToDataUrl(img, 'image/png');
    const debug: ClassifyDebug = {
      raw: png(image),
      stages,
      preprocessed: png(image),
      fed: png(fedImage),
      chosenDeg: bestDeg,
      rotations,
      topProb: best.conf,
      energy: best.energy,
      oodMsp: this.meta.oodMsp,
      oodEnergy: this.meta.oodEnergy,
      ood,
    };

    const experts = best.order.slice(0, 3).map(([i, p]) => ({ type: this.meta.labels[i], prob: p }));

    // "I don't know": reject far-off inputs by low confidence OR high energy.
    if (ood) {
      return {
        prediction: {
          type: 'unknown',
          label: 'Not recognized',
          confidence: 'low',
          ood: true,
          experts,
        },
        debug,
      };
    }

    const type = this.meta.labels[best.top];
    const conf = best.conf;
    return {
      prediction: {
        type,
        label: documentTypeLabel(type),
        // Buckets tuned to this model's scale (label-smoothed → compressed probs).
        confidence: conf >= 0.5 ? 'high' : conf >= 0.32 ? 'medium' : 'low',
        ood: false,
        experts,
      },
      debug,
    };
  }

  /** Run the model on one image and return logits/probs ranked. */
  private async infer(img: ImagePixels): Promise<InferResult> {
    const chw = this.preprocess(img);
    const logits = await this.model.run(chw, this.meta.inputSize);
    const order = softmax(logits)
      .map((p, i) => [i, p] as [number, number])
      .sort((a, b) => b[1] - a[1]);
    return { order, top: order[0][0], conf: order[0][1], logits, energy: energyScore(logits) };
  }

  /** "I don't know" test: low top probability OR high (far-off) energy. */
  private isOutOfDist(r: InferResult): boolean {
    return (
      (this.meta.oodMsp != null && r.conf < this.meta.oodMsp) ||
      (this.meta.oodEnergy != null && r.energy > this.meta.oodEnergy)
    );
  }

  /** Square-resize an image to the model input and normalise to CHW float32. */
  private preprocess(img: ImagePixels): Float32Array {
    const S = this.meta.inputSize;
    const small = resizeImage(img, S, S);
    const data = small.data;
    const plane = S * S;
    const out = new Float32Array(3 * plane);
    const { mean, std } = this.meta;
    for (let i = 0; i < plane; i++) {
      out[i] = (data[i * 4] / 255 - mean[0]) / std[0];
      out[plane + i] = (data[i * 4 + 1] / 255 - mean[1]) / std[1];
      out[2 * plane + i] = (data[i * 4 + 2] / 255 - mean[2]) / std[2];
    }
    return out;
  }
}
